import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ScanHistoryPoint } from '../api/types';
import { money, nextWeek, shortDate, weekStart } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, SERIES_ALL_PAIRS_SAFE } from './palette';

const OUR = SERIES_ALL_PAIRS_SAFE[0];
const MIN = SERIES_ALL_PAIRS_SAFE[1];
const AVG = SERIES_ALL_PAIRS_SAFE[2];
/* The band is the market low's own hue: it is the same measure widened to its
   spread, not a new category, so it must not spend a new slot. */
const BAND = SERIES_ALL_PAIRS_SAFE[1];

/**
 * Our price and the market, over time, on one euro axis. Everything is euros, so
 * everything shares the scale — a second y-axis would let the reader invent a
 * relationship that is not in the data.
 *
 * The market low and high are drawn as a band rather than as two more lines. A
 * fourth line would need a fourth hue and would read as a fourth independent
 * measure, when low and high are really the two edges of one thing: the spread
 * of offers at that moment. The band also makes "our price sits inside/outside
 * the market" legible at a glance.
 */
type WeekRow = {
  week: string;
  label: string;
  scans: number;
  firstScanAt: string | null;
  lastScanAt: string | null;
  ourPrice: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  avgPrice: number | null;
  ourRank: number | null;
  offerCount: number | null;
  marketBand: [number, number] | null;
};

/**
 * One point per calendar week, with every week between the first and last scan
 * present even when nothing was scanned in it — otherwise a crawl outage would
 * collapse into a single step and the timeline would lie about how fast prices
 * moved.
 *
 * Within a week: our price and rank come from the LAST scan, because they are
 * states rather than flows, while the market low and high are the week's
 * extremes — the range the market actually spanned. The tooltip says how many
 * scans went into each point so an aggregate is never mistaken for a reading.
 */
function toWeeks(history: ScanHistoryPoint[]): WeekRow[] {
  const buckets = new Map<string, ScanHistoryPoint[]>();

  for (const point of history) {
    const key = weekStart(point.scannedAt);
    const bucket = buckets.get(key);

    if (bucket === undefined) {
      buckets.set(key, [point]);
    } else {
      bucket.push(point);
    }
  }

  const keys = [...buckets.keys()].sort();

  if (keys.length === 0) {
    return [];
  }

  const rows: WeekRow[] = [];
  const last = keys[keys.length - 1];

  for (let week = keys[0]; ; week = nextWeek(week)) {
    const scans = buckets.get(week) ?? [];
    const numbers = (pick: (point: ScanHistoryPoint) => number | null): number[] =>
      scans.map(pick).filter((value): value is number => value !== null).map(Number);

    const lows = numbers((point) => point.minPrice);
    const highs = numbers((point) => point.maxPrice);
    const means = numbers((point) => point.avgPrice);
    const newest = scans.length > 0 ? scans[scans.length - 1] : null;

    const minPrice = lows.length > 0 ? Math.min(...lows) : null;
    const maxPrice = highs.length > 0 ? Math.max(...highs) : null;

    rows.push({
      week,
      label: shortDate(week),
      scans: scans.length,
      firstScanAt: scans.length > 0 ? scans[0].scannedAt : null,
      lastScanAt: newest === null ? null : newest.scannedAt,
      ourPrice: newest === null || newest.ourPrice === null ? null : Number(newest.ourPrice),
      minPrice,
      maxPrice,
      avgPrice: means.length > 0 ? means.reduce((sum, value) => sum + value, 0) / means.length : null,
      ourRank: newest === null ? null : newest.ourRank,
      offerCount: newest === null ? null : newest.offerCount,
      marketBand: minPrice === null || maxPrice === null ? null : [minPrice, maxPrice],
    });

    if (week === last) {
      break;
    }
  }

  return rows;
}

export function PriceHistoryChart({ history }: { history: ScanHistoryPoint[] }) {
  const rows = toWeeks(history);

  if (rows.length < 2) {
    return (
      <div className="state">
        <div>Only {history.length} scan so far.</div>
        <div className="muted">
          A trend appears once this product has been scanned in two different weeks.
        </div>
      </div>
    );
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={62}
            /* Two decimals: competitor prices often differ by cents, and whole
               euros collapse the ticks into repeated labels. */
            tickFormatter={(value: number) => `${value.toFixed(2)} €`}
            domain={['auto', 'auto']}
          />
          <Tooltip
            cursor={{ stroke: CHROME.inkMuted, strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as WeekRow;

              return (
                <ChartTooltip
                  title={`Week of ${shortDate(row.week)}`}
                  note={
                    row.scans === 0
                      ? 'No scan in this week'
                      : `${row.scans} scan${row.scans === 1 ? '' : 's'}, newest ${shortDate(
                          row.lastScanAt as string,
                        )}`
                  }
                  rows={[
                    { key: 'our', label: 'Our price', value: money(row.ourPrice), color: OUR },
                    { key: 'min', label: 'Market low', value: money(row.minPrice), color: MIN },
                    { key: 'avg', label: 'Market mean', value: money(row.avgPrice), color: AVG },
                    { key: 'max', label: 'Market high', value: money(row.maxPrice), color: BAND },
                    { key: 'rank', label: 'Our rank', value: row.ourRank === null ? '—' : `#${row.ourRank}` },
                    { key: 'offers', label: 'Offers', value: String(row.offerCount ?? '—') },
                  ]}
                />
              );
            }}
          />
          <Area
            isAnimationActive={false}
            type="monotone"
            dataKey="marketBand"
            stroke="none"
            fill={BAND}
            fillOpacity={0.16}
            /* Deliberately NOT connectNulls. A line bridging a gap reads as
               interpolation, which is conventional; a filled band bridging one
               asserts we knew the market's range in a week nobody scanned. */
            activeDot={false}
          />
          <Line isAnimationActive={false}
            type="monotone"
            dataKey="ourPrice"
            stroke={OUR}
            strokeWidth={2}
            dot={{ r: 3, fill: OUR, stroke: CHROME.surface, strokeWidth: 2 }}
            activeDot={{ r: 5, fill: OUR, stroke: CHROME.surface, strokeWidth: 2 }}
            connectNulls
          />
          <Line isAnimationActive={false}
            type="monotone"
            dataKey="minPrice"
            stroke={MIN}
            strokeWidth={2}
            dot={{ r: 3, fill: MIN, stroke: CHROME.surface, strokeWidth: 2 }}
            activeDot={{ r: 5, fill: MIN, stroke: CHROME.surface, strokeWidth: 2 }}
            connectNulls
          />
          <Line isAnimationActive={false}
            type="monotone"
            dataKey="avgPrice"
            stroke={AVG}
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            activeDot={{ r: 5, fill: AVG, stroke: CHROME.surface, strokeWidth: 2 }}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'Our price', color: OUR },
          { label: 'Market low', color: MIN },
          { label: 'Market mean', color: AVG },
          { label: 'Market low to high in the week', color: BAND, muted: true },
        ]}
      />
    </>
  );
}
